import { useCallback, useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { ChunkedRecorder, RecordedChunk } from '../utils/media/ChunkedRecorder';

const DEFAULT_VIDEO_BITS_PER_SECOND = 2500000;
const DEFAULT_AUDIO_BITS_PER_SECOND = 128000;

/** react-webcam only exposes its stream after the permission prompt is answered. */
const STREAM_POLL_INTERVAL_MS = 100;
const STREAM_WAIT_TIMEOUT_MS = 20000;

export interface UseVideoCaptureOptions {
    /** Prefix for the default `<prefix>_stream_<index>.<extension>` file name. */
    prefix?: string;
    /** Sequence number of the first chunk. Defaults to 1. */
    startIndex?: number;
    videoBitsPerSecond?: number;
    audioBitsPerSecond?: number;
    /** Overrides the default file name entirely. */
    getFileName?: (index: number, extension: string) => string;
    /** Called once per finished chunk. Awaited, so it can perform the upload. */
    onChunk?: (chunk: RecordedChunk) => void | Promise<void>;
    onError?: (error: Error) => void;
}

/**
 * Records the webcam preview as a sequence of uploadable files.
 *
 * Chunks are delivered through `onChunk` rather than through state: two chunks closing
 * within the same render pass would otherwise collapse into one and silently lose a
 * recording.
 */
export const useVideoCapture = (options: UseVideoCaptureOptions = {}) => {
    // Kept in a ref so the recorder — created once — always calls the current callbacks.
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const webcamRef = useRef<Webcam | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const recorderRef = useRef<ChunkedRecorder | null>(null);
    const [capturing, setCapturing] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fail = useCallback((cause: Error) => {
        setCapturing(false);
        setError(cause);
        optionsRef.current.onError?.(cause);
    }, []);

    if (!recorderRef.current) {
        recorderRef.current = new ChunkedRecorder({
            startIndex: options.startIndex,
            videoBitsPerSecond: options.videoBitsPerSecond ?? DEFAULT_VIDEO_BITS_PER_SECOND,
            audioBitsPerSecond: options.audioBitsPerSecond ?? DEFAULT_AUDIO_BITS_PER_SECOND,
            getFileName: (index, extension) => {
                const { getFileName, prefix } = optionsRef.current;
                if (getFileName) return getFileName(index, extension);
                return `${prefix ? `${prefix}_` : ''}stream_${index}.${extension}`;
            },
            onChunk: chunk => optionsRef.current.onChunk?.(chunk),
            onError: cause => fail(cause),
        });
    }

    const clearPoll = useCallback(() => {
        if (pollRef.current === null) return;
        clearInterval(pollRef.current);
        pollRef.current = null;
    }, []);

    /**
     * Starts recording, waiting for the camera stream if the user has not granted access
     * yet — an exam can begin before the permission prompt has been answered.
     */
    const startCapture = useCallback(() => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.recording) return;

        clearPoll();
        setError(null);

        const tryStart = (): boolean => {
            const stream = webcamRef.current?.stream;
            if (!stream) return false;

            recorder.start(stream);
            setCapturing(true);
            return true;
        };

        if (tryStart()) return;

        const waitingSince = Date.now();
        pollRef.current = setInterval(() => {
            if (tryStart()) {
                clearPoll();
                return;
            }

            if (Date.now() - waitingSince < STREAM_WAIT_TIMEOUT_MS) return;
            clearPoll();
            fail(new Error('Timed out waiting for camera access.'));
        }, STREAM_POLL_INTERVAL_MS);
    }, [clearPoll, fail]);

    /** Closes the current chunk and keeps recording into the next one, without a gap. */
    const nextChunk = useCallback(() => {
        recorderRef.current?.rotate();
    }, []);

    /** Stops recording. Resolves once the final chunk has been handled by `onChunk`. */
    const stopCapture = useCallback(async () => {
        clearPoll();
        setCapturing(false);
        await recorderRef.current?.stop();
    }, [clearPoll]);

    /** Declares the chunk number covering now — aligns numbering with a resumed exam. */
    const setChunkIndex = useCallback((index: number) => {
        recorderRef.current?.setIndex(index);
    }, []);

    useEffect(() => () => {
        clearPoll();
        recorderRef.current?.cancel();
    }, [clearPoll]);

    return {
        error,
        capturing,
        webcamRef,
        nextChunk,
        startCapture,
        stopCapture,
        setChunkIndex,
    };
};
